// Attaches the per-browser device id (Statsig stableID) to the rewards-participant
// call only. The backend hashes it into `reward_signup_signals.device_hash` to power
// the referral device-collusion gate — a referrer and referee sharing a device hash
// is a strong same-human self-farm signal. No-op for every other request, and when
// no stableID is available yet (the request proceeds without the header).
//
// Kept in its own module (separate from the fetch-wrapper install) so it can be
// unit-tested in isolation.

import { getStatsigStableId } from '@/lib/analytics/providers/statsig/client';

export const DEVICE_ID_HEADER_NAME = 'x-device-id';

// The one endpoint the backend reads the header on (POST /v1/rewards/participant).
const REWARDS_PARTICIPANT_PATH = '/v1/rewards/participant';

export function attachDeviceIdHeader(url: string, init: RequestInit | undefined): RequestInit {
  if (!targetsRewardsParticipant(url)) return init ?? {};
  const deviceId = getStatsigStableId();
  if (!deviceId) return init ?? {};
  const headers = new Headers(init?.headers);
  headers.set(DEVICE_ID_HEADER_NAME, deviceId);
  return { ...init, headers };
}

function targetsRewardsParticipant(url: string): boolean {
  try {
    // `url` is relative in dev (`/v1/...`) and absolute in prod — resolve against
    // the page origin so both forms parse to a comparable pathname.
    const base = typeof window === 'undefined' ? undefined : window.location.origin;
    return new URL(url, base).pathname.endsWith(REWARDS_PARTICIPANT_PATH);
  } catch {
    // SILENT-CATCH: an unparseable URL is by definition not the rewards-participant
    // endpoint, so the device-id header is simply not attached — never throw into fetch.
    return false;
  }
}
